import {
  compileSkyAuthoredRig,
  sampleSkyAuthoredPose,
  skyAuthoredPoseContact,
} from './115-sky-authored-pose.mjs';
import { prepareSkyLeviathanMesh, skyLeviathanMeshSweep } from './96-sky-leviathan-contacts.mjs';
import { SKY_WORLD } from './86-sky-world-scale.mjs';

export const SKY_CINDERHEART_SLAG = Object.freeze({ limit: 9, lifetime: 7.5, speed: 8, hp: 4 });
const compiled = new WeakMap();
const axes = ['x', 'y', 'z'];
function variants(rig) {
  if (compiled.has(rig)) return compiled.get(rig);
  const result = [0, 1, 2].map((index) => {
    const x = 0.3 + index * 0.2,
      y = index * 0.7,
      s = 0.22 + index * 0.025;
    const quaternion = [
      Math.sin(x / 2) * Math.cos(y / 2),
      Math.cos(x / 2) * Math.sin(y / 2),
      -Math.sin(x / 2) * Math.sin(y / 2),
      Math.cos(x / 2) * Math.cos(y / 2),
    ];
    const transform = { position: [0, 0, 0], quaternion, scale: [s, s, s] };
    const rest = sampleSkyAuthoredPose(rig, null, 0, transform);
    transform.position = rest.bounds.min.map((v, i) => -(v + rest.bounds.max[i]) / 2);
    const pose = sampleSkyAuthoredPose(rig, null, 0, transform),
      triangles = [];
    for (const mesh of pose.meshes)
      for (let i = 0; i < mesh.indices.length; i += 3) {
        const points = mesh.indices.slice(i, i + 3).map((id) =>
          Object.freeze({
            x: mesh.vertices[id * 3],
            y: mesh.vertices[id * 3 + 1],
            z: mesh.vertices[id * 3 + 2],
          }),
        );
        triangles.push(Object.freeze({ a: points[0], b: points[1], c: points[2] }));
      }
    Object.freeze(triangles);
    if (!prepareSkyLeviathanMesh(triangles)) throw new Error('Invalid source slag surface');
    return { pose, triangles, transform };
  });
  compiled.set(rig, result);
  return result;
}

/** Prepare bounded rigid source-rock poses and their actual triangle indexes
 * during readiness. Coordinates stay in source metres; uniform source scaling
 * determines both the visible 1.65–2.025m rock and its collision, never a proxy. */
export function installSkyCinderheartSlagRig(state, source) {
  const rig = compileSkyAuthoredRig(source);
  if (
    !state ||
    rig?.worldId !== 'cinderheart-slag' ||
    rig.meshes.some((mesh) => mesh.jointNodes) ||
    rig.clips.length
  )
    return false;
  try {
    variants(rig);
  } catch {
    return false;
  }
  state.authoredPropRigs ??= {};
  state.authoredPropRigs['cinderheart-slag'] = rig;
  return true;
}

/** Tag only newly emitted, real molten projectiles. Cooling occurs at 55% of
 * their initial physical flight time, capped at .85s; blocked/removed shots can
 * never create an obstacle later. One existing projectile creates at most one. */
export function markSkyCinderheartMolten(state, first) {
  for (let i = first; i < state.threats.length; i++) {
    const bolt = state.threats[i];
    if (bolt.sourceId !== state.boss?.id || !(bolt.vz > 0)) continue;
    bolt.cinderMolten = true;
    bolt.coolAt = Math.min(0.85, Math.max(0.2, ((SKY_WORLD.pilotZ - bolt.z) / bolt.vz) * 0.55));
  }
}
function position(rock) {
  rock.rootTransform.position = rock.localTransform.position.map(
    (v, i) => v + rock.poseOrigin[axes[i]],
  );
  Object.assign(rock, rock.poseOrigin);
  rock.body = {
    ...rock.poseOrigin,
    z: rock.z + rock.authoredPose.bounds.max[2],
    radius: rock.radius,
  };
}
function create(state, bolt, rig) {
  const model = variants(rig)[bolt.id % 3],
    origin = { x: bolt.x, y: bolt.y, z: bolt.z };
  const rock = {
    id: state.nextId++,
    kind: 'authored-prop',
    artVariant: 'cinderheart-slag',
    worldId: 'cinderheart-slag',
    authoredRig: rig,
    authoredPose: model.pose,
    authoredLocalFrame: true,
    triangles: model.triangles,
    localTransform: model.transform,
    rootTransform: {
      position: [],
      quaternion: [...model.transform.quaternion],
      scale: [...model.transform.scale],
    },
    poseOrigin: origin,
    age: 0,
    hp: SKY_CINDERHEART_SLAG.hp,
    maxHp: SKY_CINDERHEART_SLAG.hp,
    radius: Math.max(...model.pose.bounds.max.map((v, i) => (v - model.pose.bounds.min[i]) / 2)),
    collisionEnabled: true,
    cinderheartSlag: true,
    sourceProjectileId: bolt.id,
    slagHeat: 1,
  };
  position(rock);
  return rock;
}
const local = (point, origin) =>
  Object.fromEntries(axes.map((axis) => [axis, point[axis] - origin[axis]]));

/** Shared exact rock contact in its translated rigid frame. Rendering applies
 * the same rootTransform; cached source vertices remain immutable as it drifts.
 * Rotated/scaled silhouette holes and grazing misses remain genuinely empty. */
export function skyCinderheartSlagContact(rock, from, to) {
  if (
    !rock?.poseOrigin ||
    ![from, to].every((point) => point && axes.every((axis) => Number.isFinite(point[axis])))
  )
    return null;
  const hit = skyAuthoredPoseContact(
    rock.authoredPose,
    local(from, rock.poseOrigin),
    local(to, rock.poseOrigin),
  );
  return hit
    ? {
        ...hit,
        ...Object.fromEntries(axes.map((axis) => [axis, hit[axis] + rock.poseOrigin[axis]])),
        region: 'armor',
      }
    : null;
}

/** Advance only active simulation seconds. Cooled rocks drift slowly, accept
 * physical weapon damage, and expire after 7.5s or leaving behind the pilot.
 * Pilot contact is a continuous relative-motion sphere sweep against original
 * triangles, including edges; neither an AABB nor a visual sprite deals damage. */
export function stepSkyCinderheartSlag(state, dt, previousPilot, { hit, burst }) {
  const boss = state.boss,
    rig = state.authoredPropRigs?.['cinderheart-slag'];
  if (state.status !== 'playing' || !boss?.cinderheart || !rig || !(dt > 0) || !Number.isFinite(dt))
    return;
  const live = boss.authoredProps.filter((prop) => prop.cinderheartSlag && prop.hp > 0);
  for (const bolt of state.threats) {
    if (
      !bolt.cinderMolten ||
      bolt.sourceId !== boss.id ||
      bolt.age < bolt.coolAt ||
      bolt.z >= SKY_WORLD.pilotZ ||
      live.length >= SKY_CINDERHEART_SLAG.limit
    )
      continue;
    const rock = create(state, bolt, rig);
    live.push(rock);
    boss.authoredProps.push(rock);
    bolt.cooled = true;
    boss.cinderheart.cooledProjectiles = (boss.cinderheart.cooledProjectiles ?? 0) + 1;
  }
  state.threats = state.threats.filter((bolt) => !bolt.cooled);
  const before = { ...previousPilot, z: previousPilot.z ?? SKY_WORLD.pilotZ },
    after = { x: state.x, y: state.y, z: state.pilotZ ?? SKY_WORLD.pilotZ };
  for (const rock of live) {
    const old = { ...rock.poseOrigin };
    rock.age += dt;
    rock.poseOrigin.z += dt * SKY_CINDERHEART_SLAG.speed;
    rock.slagHeat = Math.max(0, 1 - rock.age / 2);
    position(rock);
    const contact = skyLeviathanMeshSweep(
      local(before, old),
      local(after, rock.poseOrigin),
      rock.triangles,
      SKY_WORLD.pilotContactRadius,
    );
    if (contact) {
      hit(state, 'cooled-magma');
      rock.hp = 0;
      burst(state, rock, 'red');
    }
    if (state.status !== 'playing') break;
  }
  boss.authoredProps = boss.authoredProps.filter(
    (prop) =>
      !prop.cinderheartSlag ||
      (prop.hp > 0 && prop.age < SKY_CINDERHEART_SLAG.lifetime && prop.z < 6),
  );
}

/** Real source-mesh impacts chip only the intersected rock. No scoring or
 * guardian HP is fabricated;81 owns shot/accuracy bookkeeping once per impact. */
export function resolveSkyCinderheartSlagContact(target, { damage }) {
  if (!target.cinderheartSlag) return undefined;
  const removed = Math.min(target.hp, Math.max(0, Number.isFinite(damage) ? damage : 0));
  target.hp -= removed;
  return { handled: true, damage: removed, quality: 'body', disabled: target.hp <= 0 };
}
